import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Container, 
  Row, 
  Col, 
  Button, 
  Badge, 
  Spinner, 
  Table,
  Pagination,
  Form,
  InputGroup
} from 'react-bootstrap';
import { 
  FaStethoscope, 
  FaSearch, 
  FaUser, 
  FaPhone, 
  FaEnvelope,
  FaMapMarkerAlt,
  FaGraduationCap
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

export default function AdminDoctors() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [specialties, setSpecialties] = useState([]);

  useEffect(() => {
    if (user) {
      fetchDoctors();
      fetchSpecialties();
    }
  }, [user, currentPage, specialtyFilter]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDoctors(currentPage, 10, specialtyFilter, searchTerm);
      if (response.success) {
        setDoctors(response.data.doctors || response.data);
        setTotalPages(Math.ceil((response.data.total || response.data.length) / 10));
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialties = async () => {
    try {
      const response = await apiService.getDoctorSpecialties();
      if (response.success) {
        setSpecialties(response.data);
      }
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDoctors();
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'secondary';
      case 'pending':
        return 'warning';
      default:
        return 'primary';
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "#f8fffe" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading doctors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <Container className="py-5">
        {/* Header */}
        <div className="mb-5">
          <div
            className="p-5 rounded-4 text-white position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
            }}
          >
            <Row className="align-items-center position-relative">
              <Col md={8}>
                <div className="mb-3">
                  <Badge
                    className="px-3 py-2 rounded-pill fw-medium mb-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaStethoscope className="me-2" />
                    Doctor Management
                  </Badge>
                </div>
                <h1 className="display-5 fw-bold mb-3">Manage Doctors</h1>
                <p className="fs-5 opacity-90 mb-4">View and manage doctor profiles and schedules</p>
              </Col>
              <Col md={4} className="text-center">
                <div
                  className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                  style={{
                    width: "120px",
                    height: "120px",
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <FaStethoscope size={48} />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="border-0 rounded-4 mb-4" style={{ backgroundColor: "#f0fdfa" }}>
          <Card.Body className="p-4">
            <Row>
              <Col md={6}>
                <Form onSubmit={handleSearch}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search doctors by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button type="submit" variant="primary">
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </Form>
              </Col>
              <Col md={6}>
                <Form.Select
                  value={specialtyFilter}
                  onChange={(e) => setSpecialtyFilter(e.target.value)}
                >
                  <option value="">All Specialties</option>
                  {specialties.map((specialty) => (
                    <option key={specialty} value={specialty}>
                      {specialty}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Doctors Table */}
        <Card className="border-0 rounded-4" style={{ backgroundColor: "#f0fdfa" }}>
          <Card.Header className="bg-transparent border-0 p-4">
            <h5 className="fw-bold mb-0">Doctors List</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive className="mb-0">
              <thead style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
                <tr>
                  <th className="border-0 p-3">Doctor</th>
                  <th className="border-0 p-3">Specialty</th>
                  <th className="border-0 p-3">Contact</th>
                  <th className="border-0 p-3">Status</th>
                  <th className="border-0 p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {doctors.length > 0 ? (
                  doctors.map((doctor) => (
                    <tr key={doctor.id} style={{ backgroundColor: "white" }}>
                      <td className="p-3">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "50px",
                              height: "50px",
                              background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                            }}
                          >
                            <FaUser className="text-white" size={20} />
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1">{doctor.name}</h6>
                            <small className="text-muted">{doctor.email}</small>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <Badge bg="info" className="rounded-pill">
                            {doctor.specialty}
                          </Badge>
                        </div>
                        {doctor.qualification && (
                          <small className="text-muted d-block">
                            <FaGraduationCap className="me-1" />
                            {doctor.qualification}
                          </small>
                        )}
                      </td>
                      <td className="p-3">
                        <div>
                          <div className="d-flex align-items-center mb-1">
                            <FaPhone className="me-2 text-muted" size={12} />
                            <small>{doctor.phone}</small>
                          </div>
                          <div className="d-flex align-items-center">
                            <FaEnvelope className="me-2 text-muted" size={12} />
                            <small>{doctor.email}</small>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge bg={getStatusVariant(doctor.status)} className="rounded-pill">
                          {doctor.status || 'Active'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Button variant="outline-primary" size="sm" className="me-2">
                          View Profile
                        </Button>
                        <Button variant="outline-success" size="sm">
                          Schedule
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <FaStethoscope size={48} className="text-muted mb-3" />
                      <h6 className="text-muted">No doctors found</h6>
                      <p className="text-muted">No doctors match your search criteria</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.First 
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
              />
              <Pagination.Prev 
                onClick={() => setCurrentPage(currentPage - 1)} 
                disabled={currentPage === 1}
              />
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Pagination.Item>
              ))}
              
              <Pagination.Next 
                onClick={() => setCurrentPage(currentPage + 1)} 
                disabled={currentPage === totalPages}
              />
              <Pagination.Last 
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Container>
    </div>
  );
}
